import React, { useState } from 'react';
import {
  FEED_CARD_SHELL,
  FEED_TITLE,
} from './investigation/feedCardStyles';
import MatchBadge from './investigation/MatchBadge';
import { PIVOT_CTA_CLASS } from './investigation/TargetPivotsCard';

function badgeTone(ok) {
  return ok
    ? 'border-emerald-400/50 bg-emerald-950/70 text-emerald-100'
    : 'border-amber-400/50 bg-amber-950/55 text-amber-100';
}

/**
 * Compact email deliverability / MX status card (all domain types).
 *
 * @param {{
 *   data: object,
 *   personPivot?: {
 *     eligible?: boolean,
 *     personName?: string,
 *     companyName?: string,
 *     location?: string,
 *     domain?: string,
 *     label?: string,
 *   } | null,
 *   onPivotToPerson?: Function,
 * }} props
 */
export default function DeliverabilityCard({ data, personPivot = null, onPivotToPerson = null }) {
  const [mxOpen, setMxOpen] = useState(false);
  if (!data) return null;

  const formatOk = String(data.format || '').toLowerCase() === 'valid';
  const typeLabel = data.type_label || String(data.type || 'corporate');
  const smtpLabel = data.smtp_server_status_label || String(data.smtp_server_status || 'skipped');
  const smtpOk = String(data.smtp_server_status || '').toLowerCase() === 'valid';
  const mailboxLabel =
    data.mailbox_status_label ||
    (data.mailbox_exists ? 'Valid / Reachable' : String(data.mailbox_status || 'unknown'));
  const mailboxOk =
    String(data.mailbox_status || '').toLowerCase() === 'valid' || data.mailbox_exists === true;
  const mx = Array.isArray(data.mx_records) ? data.mx_records.filter(Boolean) : [];
  const primaryMx = data.mx_primary || mx[0] || '';
  const backups = mx.slice(1);
  const backupCount =
    typeof data.mx_backup_count === 'number' ? data.mx_backup_count : backups.length;
  const score = Number(data.confidence_score) || 0;
  const showPivot =
    Boolean(personPivot?.eligible) &&
    Boolean(personPivot?.personName) &&
    typeof onPivotToPerson === 'function';

  return (
    <article className={FEED_CARD_SHELL} data-testid="email-deliverability-card">
      <section className="flex flex-col gap-4">
        <div className="flex min-w-0 items-center gap-4">
          <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl border border-emerald-500/30 bg-emerald-950/40 text-2xl shadow-inner">
            ✉
          </span>
          <div className="min-w-0 flex-1">
            <h4 className="text-[11px] font-black uppercase tracking-[0.12em] text-emerald-200 sm:text-xs sm:tracking-[0.14em]">
              Email Deliverability & MX Status
            </h4>
            <div className="mt-0.5 flex min-w-0 flex-wrap items-center gap-2">
              <p className={`truncate ${FEED_TITLE}`}>{data.email || 'Target mailbox'}</p>
              <MatchBadge value={score} />
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <span
            className={`rounded-lg border px-2.5 py-1 text-[11px] font-black uppercase tracking-wide sm:text-xs sm:tracking-wider ${badgeTone(formatOk)}`}
          >
            Format: {data.format_label || (formatOk ? 'Valid' : 'Invalid')}
          </span>
          <span
            className={`rounded-lg border px-2.5 py-1 text-[11px] font-black uppercase tracking-wide sm:text-xs sm:tracking-wider ${badgeTone(true)}`}
          >
            Type: {typeLabel}
          </span>
          <span
            className={`rounded-lg border px-2.5 py-1 text-[11px] font-black uppercase tracking-wide sm:text-xs sm:tracking-wider ${badgeTone(smtpOk)}`}
          >
            Server Status: {smtpLabel}
          </span>
          <span
            className={`rounded-lg border px-2.5 py-1 text-[11px] font-black uppercase tracking-wide sm:text-xs sm:tracking-wider ${badgeTone(mailboxOk)}`}
          >
            Mailbox: {mailboxLabel}
          </span>
        </div>

        {primaryMx ? (
          <div className="rounded-xl border border-white/10 bg-slate-950/80 px-3 py-2">
            <span className="mb-1.5 block text-[10px] font-black uppercase tracking-wider text-slate-400 sm:text-[11px]">
              MX Records
            </span>
            <div className="flex flex-wrap items-center gap-2 font-mono text-[11px] text-slate-200 sm:text-xs">
              <span className="min-w-0 truncate">
                <span className="text-slate-400">Primary MX:</span> {primaryMx}
              </span>
              {backupCount > 0 ? (
                <button
                  type="button"
                  onClick={() => setMxOpen((v) => !v)}
                  className="rounded-md border border-slate-600/50 bg-slate-900/80 px-2 py-0.5 text-[10px] font-black uppercase tracking-wider text-slate-300 transition hover:border-emerald-500/40 hover:text-emerald-200 sm:text-[11px]"
                  aria-expanded={mxOpen}
                  title={backups.join(', ')}
                >
                  +{backupCount} backup server{backupCount === 1 ? '' : 's'}
                </button>
              ) : null}
            </div>
            {mxOpen && backups.length > 0 ? (
              <ul className="mt-2 space-y-0.5 border-t border-white/5 pt-2 font-mono text-[11px] text-slate-300 sm:text-xs">
                {backups.map((host) => (
                  <li key={host} className="truncate">
                    {host}
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}

        {showPivot ? (
          <div className="flex flex-wrap items-center gap-2 border-t border-white/5 pt-3">
            <button
              type="button"
              data-testid="email-pivot-to-person"
              className={PIVOT_CTA_CLASS}
              onClick={() =>
                onPivotToPerson({
                  personName: personPivot.personName,
                  companyName: personPivot.companyName || '',
                  domain: personPivot.domain || '',
                  locations: personPivot.location ? [personPivot.location] : [],
                  contextTags: personPivot.companyName ? [personPivot.companyName] : [],
                })
              }
            >
              <span aria-hidden>👤</span>
              {personPivot.label || `Pivot to Person: ${personPivot.personName}`}
            </button>
          </div>
        ) : null}
      </section>
    </article>
  );
}
